<template>
	<div>
		<Breadcrumbs main="App" title="Role Management" />
		<div class="container-fluid">

			<!-- Roles Card -->
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>Roles</h5>
								<span>Define roles and assign permissions to each role.</span>
							</div>
							<div class="d-flex gap-2">
								<button class="btn btn-outline-primary" type="button" @click="fetchRoles" :disabled="loading">
									Refresh
								</button>
								<button v-if="canCreate" class="btn btn-primary" type="button" @click="openCreate">
									New Role
								</button>
							</div>
						</div>
						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">{{ successMessage }}</div>

							<div class="mb-3 row align-items-center">
								<label class="col-sm-2 col-form-label">Search</label>
								<div class="col-sm-10">
									<input
										v-model="searchTerm"
										class="form-control"
										type="text"
										placeholder="Filter by role name or description"
									/>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered">
									<thead>
										<tr>
											<th>#</th>
											<th>Name</th>
											<th>Description</th>
											<th class="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="4" class="text-center">Loading...</td>
										</tr>
										<tr v-else-if="!filteredRoles.length">
											<td colspan="4" class="text-center">No roles found.</td>
										</tr>
										<tr
											v-for="role in filteredRoles"
											:key="role.id"
											:class="{ 'table-active': selectedRole && selectedRole.id === role.id }"
										>
											<td>{{ role.id }}</td>
											<td>{{ role.name }}</td>
											<td>{{ role.description || '—' }}</td>
											<td class="text-center text-nowrap">
												<button
													class="btn btn-sm btn-outline-info mr-1"
													@click="selectRole(role)"
													:title="'Manage permissions for ' + role.name"
												>
													Permissions
												</button>
												<button
													v-if="canUpdate"
													class="btn btn-sm btn-outline-secondary mr-1"
													@click="openEdit(role)"
												>
													Edit
												</button>
												<button
													v-if="canDelete"
													class="btn btn-sm btn-outline-danger"
													@click="deleteRole(role)"
												>
													Delete
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Permissions Panel (shown when a role is selected) -->
			<div class="row" v-if="selectedRole">
				<div class="col-12">
					<div class="card border-info">
						<div class="card-header d-flex justify-content-between align-items-center bg-light">
							<div>
								<h5 class="mb-0">
									Permissions —
									<span class="text-info">{{ selectedRole.name }}</span>
								</h5>
								<small class="text-muted">{{ selectedRole.description }}</small>
							</div>
							<button type="button" class="close" @click="selectedRole = null">
								<span>&times;</span>
							</button>
						</div>
						<div class="card-body">
							<div v-if="permError" class="alert alert-danger">{{ permError }}</div>
							<div v-if="permSuccess" class="alert alert-success">{{ permSuccess }}</div>

							<!-- Add permission form -->
							<div v-if="canUpdate" class="mb-4">
								<h6>Add Permission</h6>
								<form @submit.prevent="addPermission" class="form-row align-items-end">
									<div class="col-sm-4 form-group mb-0">
										<label>Permission Code <span class="text-danger">*</span></label>
										<input
											v-model="newPerm.permissionCode"
											class="form-control"
											type="text"
											placeholder="e.g. draw:read"
											required
										/>
									</div>
									<div class="col-sm-5 form-group mb-0">
										<label>Description</label>
										<input
											v-model="newPerm.description"
											class="form-control"
											type="text"
											placeholder="Optional description"
										/>
									</div>
									<div class="col-sm-3 form-group mb-0">
										<label class="d-block">&nbsp;</label>
										<button
											type="submit"
											class="btn btn-success btn-block"
											:disabled="addingPerm"
										>
											{{ addingPerm ? 'Adding...' : 'Add Permission' }}
										</button>
									</div>
								</form>
							</div>

							<!-- Permissions list -->
							<div v-if="permsLoading" class="text-center py-2">Loading permissions...</div>
							<div v-else-if="!rolePermissions.length" class="text-muted">
								No permissions assigned to this role yet.
							</div>
							<div v-else>
								<h6>Assigned Permissions</h6>
								<div class="d-flex flex-wrap gap-2">
									<div
										v-for="perm in rolePermissions"
										:key="perm.permissionCode || perm"
										class="d-flex align-items-center badge-perm"
									>
										<span class="badge badge-primary mr-1 p-2">
											{{ perm.permissionCode || perm }}
										</span>
										<span v-if="perm.description" class="text-muted small mr-2">
											{{ perm.description }}
										</span>
										<button
											v-if="canUpdate"
											type="button"
											class="btn btn-sm btn-link text-danger p-0"
											title="Remove permission"
											@click="removePermission(perm.permissionCode || perm)"
										>
											&times;
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Create / Edit Role Modal -->
		<div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
			<div class="modal-dialog-custom card">
				<div class="card-header d-flex justify-content-between align-items-center">
					<h5 class="mb-0">{{ isEditing ? 'Edit Role' : 'New Role' }}</h5>
					<button type="button" class="close" @click="closeModal"><span>&times;</span></button>
				</div>
				<div class="card-body">
					<div v-if="formError" class="alert alert-danger">{{ formError }}</div>
					<form @submit.prevent="saveRole">
						<div class="form-group">
							<label>Name <span class="text-danger">*</span></label>
							<input
								v-model="form.name"
								type="text"
								class="form-control"
								placeholder="e.g. Admin"
								required
							/>
						</div>
						<div class="form-group">
							<label>Description</label>
							<input
								v-model="form.description"
								type="text"
								class="form-control"
								placeholder="Brief description of this role"
							/>
						</div>
						<div class="d-flex justify-content-end gap-2 mt-3">
							<button type="button" class="btn btn-secondary mr-2" @click="closeModal">Cancel</button>
							<button type="submit" class="btn btn-primary" :disabled="saving">
								{{ saving ? 'Saving...' : (isEditing ? 'Update Role' : 'Create Role') }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';

export default {
	name: 'RoleIndex',

	data() {
		return {
			roles: [],
			loading: false,
			saving: false,
			searchTerm: '',
			errorMessage: '',
			successMessage: '',

			// Role modal
			showModal: false,
			isEditing: false,
			editingId: null,
			form: { name: '', description: '' },
			formError: '',

			// Permissions panel
			selectedRole: null,
			rolePermissions: [],
			permsLoading: false,
			permError: '',
			permSuccess: '',
			addingPerm: false,
			newPerm: { permissionCode: '', description: '' },
		};
	},

	computed: {
		permissions() {
			return this.$store.state.auth?.permissions || [];
		},
		canCreate() {
			return this.permissions.includes('role:create');
		},
		canUpdate() {
			return this.permissions.includes('role:update');
		},
		canDelete() {
			return this.permissions.includes('role:delete');
		},
		filteredRoles() {
			if (!this.searchTerm.trim()) return this.roles;
			const q = this.searchTerm.trim().toLowerCase();
			return this.roles.filter(r =>
				(r.name || '').toLowerCase().includes(q) ||
				(r.description || '').toLowerCase().includes(q)
			);
		},
	},

	mounted() {
		this.fetchRoles();
	},

	methods: {
		authHeaders(includeJson = true) {
			const token = localStorage.getItem('token');
			const headers = {};
			if (token) headers['Authorization'] = `Bearer ${token}`;
			if (includeJson) headers['Content-Type'] = 'application/json';
			return headers;
		},

		showSuccess(msg) {
			this.successMessage = msg;
			this.errorMessage = '';
			setTimeout(() => { this.successMessage = ''; }, 4000);
		},

		showError(msg) {
			this.errorMessage = msg;
			this.successMessage = '';
		},

		showPermSuccess(msg) {
			this.permSuccess = msg;
			this.permError = '';
			setTimeout(() => { this.permSuccess = ''; }, 4000);
		},

		async fetchRoles() {
			this.loading = true;
			this.errorMessage = '';
			try {
				const res = await fetch(`${API_BASE}/api/Role`, { headers: this.authHeaders(false) });
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const data = await res.json();
				this.roles = Array.isArray(data) ? data : (data.data || data.items || []);
			} catch (err) {
				this.showError('Failed to load roles: ' + err.message);
			} finally {
				this.loading = false;
			}
		},

		openCreate() {
			this.isEditing = false;
			this.editingId = null;
			this.form = { name: '', description: '' };
			this.formError = '';
			this.showModal = true;
		},

		openEdit(role) {
			this.isEditing = true;
			this.editingId = role.id;
			this.form = { name: role.name || '', description: role.description || '' };
			this.formError = '';
			this.showModal = true;
		},

		closeModal() {
			this.showModal = false;
		},

		async saveRole() {
			this.formError = '';
			this.saving = true;
			try {
				const url = this.isEditing
					? `${API_BASE}/api/Role/${this.editingId}`
					: `${API_BASE}/api/Role`;
				const method = this.isEditing ? 'PUT' : 'POST';

				const res = await fetch(url, {
					method,
					headers: this.authHeaders(),
					body: JSON.stringify({ name: this.form.name, description: this.form.description }),
				});

				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try { const e = await res.json(); errMsg = e.message || e.title || JSON.stringify(e); } catch { /* ignore */ }
					this.formError = errMsg;
					return;
				}

				this.closeModal();
				this.showSuccess(this.isEditing ? 'Role updated successfully.' : 'Role created successfully.');
				await this.fetchRoles();

				// Refresh permissions panel if the edited role is currently selected
				if (this.selectedRole && this.selectedRole.id === this.editingId) {
					const updated = this.roles.find(r => r.id === this.editingId);
					if (updated) this.selectedRole = updated;
				}
			} catch (err) {
				this.formError = 'Request failed: ' + err.message;
			} finally {
				this.saving = false;
			}
		},

		async deleteRole(role) {
			if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
			try {
				const res = await fetch(`${API_BASE}/api/Role/${role.id}`, {
					method: 'DELETE',
					headers: this.authHeaders(false),
				});
				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try { const e = await res.json(); errMsg = e.message || e.title || JSON.stringify(e); } catch { /* ignore */ }
					this.showError('Delete failed: ' + errMsg);
					return;
				}
				if (this.selectedRole && this.selectedRole.id === role.id) this.selectedRole = null;
				this.showSuccess(`Role "${role.name}" deleted.`);
				await this.fetchRoles();
			} catch (err) {
				this.showError('Delete failed: ' + err.message);
			}
		},

		// --- Permissions Panel ---

		async selectRole(role) {
			this.selectedRole = role;
			this.permError = '';
			this.permSuccess = '';
			this.newPerm = { permissionCode: '', description: '' };
			await this.fetchPermissions(role.id);
		},

		async fetchPermissions(roleId) {
			this.permsLoading = true;
			this.rolePermissions = [];
			this.permError = '';
			try {
				const res = await fetch(`${API_BASE}/api/Role/${roleId}/permissions`, {
					headers: this.authHeaders(false),
				});
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				const data = await res.json();
				this.rolePermissions = Array.isArray(data) ? data : (data.data || data.items || []);
			} catch (err) {
				this.permError = 'Failed to load permissions: ' + err.message;
			} finally {
				this.permsLoading = false;
			}
		},

		async addPermission() {
			if (!this.newPerm.permissionCode.trim()) return;
			this.addingPerm = true;
			this.permError = '';
			try {
				const res = await fetch(`${API_BASE}/api/Role/${this.selectedRole.id}/permissions`, {
					method: 'POST',
					headers: this.authHeaders(),
					body: JSON.stringify({
						permissionCode: this.newPerm.permissionCode.trim(),
						description: this.newPerm.description.trim() || null,
					}),
				});
				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try { const e = await res.json(); errMsg = e.message || e.title || JSON.stringify(e); } catch { /* ignore */ }
					this.permError = errMsg;
					return;
				}
				this.newPerm = { permissionCode: '', description: '' };
				this.showPermSuccess('Permission added successfully.');
				await this.fetchPermissions(this.selectedRole.id);
			} catch (err) {
				this.permError = 'Request failed: ' + err.message;
			} finally {
				this.addingPerm = false;
			}
		},

		async removePermission(permissionCode) {
			if (!confirm(`Remove permission "${permissionCode}" from role "${this.selectedRole.name}"?`)) return;
			this.permError = '';
			try {
				const res = await fetch(
					`${API_BASE}/api/Role/${this.selectedRole.id}/permissions/${encodeURIComponent(permissionCode)}`,
					{ method: 'DELETE', headers: this.authHeaders(false) }
				);
				if (!res.ok) {
					let errMsg = `HTTP ${res.status}`;
					try { const e = await res.json(); errMsg = e.message || e.title || JSON.stringify(e); } catch { /* ignore */ }
					this.permError = 'Remove failed: ' + errMsg;
					return;
				}
				this.showPermSuccess(`Permission "${permissionCode}" removed.`);
				await this.fetchPermissions(this.selectedRole.id);
			} catch (err) {
				this.permError = 'Request failed: ' + err.message;
			}
		},
	},
};
</script>

<style scoped>
.modal-backdrop-custom {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1050;
}
.modal-dialog-custom {
	width: 100%;
	max-width: 480px;
	border-radius: 8px;
	overflow: hidden;
}
.badge-perm {
	margin-bottom: 6px;
}
.table-active td {
	background-color: rgba(0, 123, 255, 0.05);
}
</style>
